/**
 * A person, synthesised. What another player sounds like when they are not on
 * a microphone — which will be most of them, most of the time.
 *
 * This is not a fallback for the voice chat. It is the version of the feature
 * nearly everybody will actually hear, and it is the better one: a half-heard
 * voice with no words in it, two rooms away, in a building you thought you had
 * to yourself, is worth more than a friend on a clear headset. So it is built
 * as its own instrument rather than as a placeholder.
 *
 * How it is made
 * --------------
 * A sawtooth glottal source, rolled off like a real one, through two parallel
 * bandpass formants. That is a vocal tract, and it is why this reads as a mouth
 * rather than as noise with a shape. On top: a little aspiration through a
 * bandpass near the second formant, a few cents of vibrato, and a pitch contour
 * that falls across a phrase the way a sentence does. The syllables are
 * scheduled events with real silence between them, never an amplitude ripple —
 * speech has consonants, and a tremolo does not.
 *
 * Each peer gets a fixed timbre hashed from their id, so the same friend is the
 * same person every time you hear them.
 *
 * Keeping it away from the entities
 * ---------------------------------
 * The entity vocalisations live in `sfx.ts` in this same directory, and the game
 * has spent the whole run teaching the player what they mean. Nothing here may
 * be mistaken for one, so this instrument deliberately gives up the tools they
 * use:
 *
 *  - no waveshaper anywhere (the hound and the centipede are saturated),
 *  - no amplitude ripple at all, at any rate (the growl rattles at ~23 Hz, the
 *    smiler's laugh at ~7),
 *  - nothing below 80 Hz and no sweep wider than a fifth,
 *  - every event under 1.6 s, against the smiler's 2.6 s breath,
 *  - and it never rises. The level is distance and nothing else.
 *
 * What is left is quiet, intermittent, and unmistakably a person.
 */

import { clamp, lerp } from '../core/math';
import { hashSeed, makeRNG } from '../core/rng';
import type { RNG } from '../core/types';
import type { AudioGraph } from './graph';

/**
 * How much of a person we are allowed to make.
 *
 *  full  nothing is arriving from them at all, so they are a voice as well as a
 *        body: phrases, breath, movement.
 *  body  their microphone is open but they have been quiet for a while. Breath
 *        and the shift of their weight only — putting words in the mouth of
 *        somebody the game can actually hear would be a lie.
 *  off   they are talking. Their own voice is the sound they make.
 */
export type MutterMode = 'off' | 'body' | 'full';

/**
 * Vowel formant pairs, in Hz. Weighted towards the closed end of the set: a
 * mutter is made with the mouth mostly shut, and the open vowels are what make
 * a mumble read as shouting.
 */
const VOWELS: ReadonlyArray<readonly [number, number, number]> = [
  // [F1, F2, weight]
  [480, 1450, 4], // schwa — the sound of not saying anything in particular
  [360, 1000, 3], // "o"
  [320, 880, 3], // "u"
  [250, 1150, 3], // nasal "mm"
  [620, 1250, 2], // "a", the only open one
  [430, 1750, 1], // "e"
];

const VOWEL_TOTAL = VOWELS.reduce((s, v) => s + v[2], 0);

/** Seconds of tail allowed past an event before its nodes are released. */
const REAP_PAD = 0.12;

/** One peer's fixed timbre. Hashed from their id, so it never changes. */
export interface Timbre {
  /** Fundamental in Hz. Deliberately spans children and adults. */
  f0: number;
  /** Vocal-tract length as a multiplier on the formant table. */
  tract: number;
  /** Syllables per second. */
  rate: number;
  /** How much aspiration rides on the voiced part, 0..1. */
  breath: number;
  /** Multiplier on how often they make any noise at all. */
  talkative: number;
}

/**
 * Derive a peer's voice from their id. Pure and stable: two clients hearing the
 * same peer hear the same person, which matters as soon as three people are in
 * a room together.
 */
export function timbreFor(peerId: string): Timbre {
  const r = makeRNG(hashSeed(peerId));
  // Two clusters rather than one flat range, because a uniform draw puts
  // everybody in the middle and they all sound like the same stranger.
  const high = r.chance(0.45);
  return {
    f0: high ? r.range(158, 236) : r.range(86, 132),
    tract: high ? r.range(1.04, 1.2) : r.range(0.85, 1.0),
    rate: r.range(3.0, 4.7),
    breath: r.range(0.14, 0.5),
    talkative: r.range(0.65, 1.45),
  };
}

interface Scheduled {
  /** Context time after which the nodes may be released. */
  at: number;
  nodes: AudioNode[];
  sources: AudioScheduledSourceNode[];
}

/**
 * Drives one peer's human noise into a destination node.
 *
 * The destination is the peer's spatial chain (see `voicepeer.ts`), so
 * everything here is pre-distance and pre-pan: this instrument decides what the
 * person sounds like and the chain decides where they are.
 */
export class Mutter {
  private next = 2;
  private readonly pending: Scheduled[] = [];
  /** Rises while they are making noise so two phrases never run together. */
  private cooldown = 0;

  constructor(
    private readonly g: AudioGraph,
    private readonly dest: AudioNode,
    private readonly timbre: Timbre,
  ) {
    // Staggered, so a room that four people walk into does not have all four of
    // them clear their throat at once.
    this.next = this.g.rng.range(0.6, 4.5);
  }

  /**
   * `audible` is the distance gate: when the peer is past `VOICE_FAR` there is
   * nothing to schedule, and we neither spend nodes nor advance the clock into
   * a burst that would land the moment they came back into range.
   */
  update(dt: number, mode: MutterMode, audible: boolean): void {
    this.reap();
    if (mode === 'off' || !audible) {
      // Hold the timer a little way out rather than at zero, so somebody who
      // stops talking does not immediately start muttering over their own tail.
      if (this.next < 1.2) this.next = this.g.rng.range(1.5, 4);
      return;
    }

    this.cooldown = Math.max(0, this.cooldown - dt);
    this.next -= dt * this.timbre.talkative;
    if (this.next > 0 || this.cooldown > 0) return;

    const r = this.g.rng;
    const t0 = this.g.now + 0.02;
    if (mode === 'full' && r.chance(0.42)) {
      const end = this.phrase(t0);
      this.cooldown = end - this.g.now;
      // A phrase buys a long silence after it. The gaps are the instrument.
      this.next = r.range(6, 17);
    } else if (r.chance(0.5)) {
      const end = this.breath(t0);
      this.cooldown = end - this.g.now;
      this.next = r.range(3.5, 9);
    } else if (r.chance(0.55)) {
      const end = this.shift(t0);
      this.cooldown = end - this.g.now;
      this.next = r.range(3, 8);
    } else {
      const end = this.throat(t0);
      this.cooldown = end - this.g.now;
      this.next = r.range(5, 13);
    }
  }

  dispose(): void {
    for (const s of this.pending) release(s);
    this.pending.length = 0;
  }

  /* ---------------------------------------------------------------- *
   * Events
   * ---------------------------------------------------------------- */

  /**
   * Three to seven syllables with a falling contour and no words in them.
   *
   * The declination — starting a little above the speaker's pitch and ending a
   * little below it — is the single thing that makes a run of vowels read as
   * somebody saying something rather than as a machine. It is worth more than
   * the formants.
   */
  private phrase(t0: number): number {
    const r = this.g.rng;
    const tm = this.timbre;
    const n = r.int(3, 7);
    const s: Scheduled = { at: 0, nodes: [], sources: [] };

    let t = t0;
    const top = tm.f0 * r.range(1.04, 1.16);
    const bottom = tm.f0 * r.range(0.8, 0.92);
    for (let i = 0; i < n; i++) {
      const k = n === 1 ? 1 : i / (n - 1);
      // Not a straight line: the drop is mostly spent on the last two syllables.
      const pitch = lerp(top, bottom, k * k);
      const dur = (1 / tm.rate) * r.range(0.55, 0.95);
      // A run of pure vowels is a chant. Unvoiced syllables are the consonants.
      if (r.chance(0.18)) this.fricative(s, t, dur * 0.7, r.range(0.35, 0.6));
      else this.syllable(s, t, dur, pitch, r.range(0.7, 1) * (1 - k * 0.3));
      t += dur + (1 / tm.rate) * r.range(0.12, 0.45);
    }
    return this.commit(s, t);
  }

  /** One voiced syllable: glottal source, two formants, a breath of air on top. */
  private syllable(s: Scheduled, t0: number, dur: number, f0: number, level: number): void {
    const ctx = this.g.ctx;
    const r = this.g.rng;
    const tm = this.timbre;
    const attack = 0.028;
    const decay = Math.max(0.05, dur * 0.45);
    const hold = Math.max(0.02, dur - attack);
    const end = t0 + attack + hold + decay;

    const [f1, f2] = pickVowel(r, tm.tract);

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    // A few cents of drift per syllable. Perfectly steady pitch is the fastest
    // way to sound synthesised.
    osc.detune.value = r.range(-18, 18);
    osc.frequency.setValueAtTime(f0, t0);
    // Each syllable leans slightly, which is what a mouth moving sounds like.
    osc.frequency.exponentialRampToValueAtTime(clamp(f0 * r.range(0.93, 1.07), 60, 400), end);

    // A real glottal wave falls off steeply; a raw sawtooth is far too bright
    // and lands somewhere between a kazoo and a reed.
    const tilt = ctx.createBiquadFilter();
    tilt.type = 'lowpass';
    tilt.frequency.value = clamp(f0 * 13, 700, 4200);
    tilt.Q.value = 0.4;
    osc.connect(tilt);

    const sum = ctx.createGain();
    sum.gain.value = 1;
    formant(ctx, tilt, sum, f1, 7.5, 1);
    // The second formant is what carries the vowel. Under-weighting it leaves a
    // hum with a shape rather than a mouth.
    formant(ctx, tilt, sum, f2, 9.5, 0.58);

    const g = ctx.createGain();
    env(g.gain, t0, 0.3 * level, attack, hold, decay);
    sum.connect(g);
    g.connect(this.dest);

    const vib = ctx.createOscillator();
    vib.frequency.value = r.range(4.2, 6.4);
    const vibAmp = ctx.createGain();
    vibAmp.gain.value = r.range(5, 14);
    vib.connect(vibAmp);
    vibAmp.connect(osc.detune);

    osc.start(t0);
    osc.stop(end + 0.02);
    vib.start(t0);
    vib.stop(end + 0.02);
    s.sources.push(osc, vib);
    s.nodes.push(tilt, sum, g, vibAmp);

    if (tm.breath > 0.05) {
      const air = this.noise('pink', t0, end);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = clamp(f2 * 1.15, 300, 6000);
      bp.Q.value = 1.6;
      const ag = ctx.createGain();
      env(ag.gain, t0, 0.05 * level * tm.breath, attack * 1.6, hold, decay);
      air.connect(bp);
      bp.connect(ag);
      ag.connect(this.dest);
      s.sources.push(air);
      s.nodes.push(bp, ag);
    }
  }

  /** An unvoiced syllable — an "s", "f" or "th". Noise only; no pitch. */
  private fricative(s: Scheduled, t0: number, dur: number, level: number): void {
    const ctx = this.g.ctx;
    const r = this.g.rng;
    const attack = 0.018;
    const decay = 0.05;
    const hold = Math.max(0.02, dur - attack);
    const end = t0 + attack + hold + decay;

    const src = this.noise('white', t0, end);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = r.range(2400, 5200) * this.timbre.tract;
    bp.Q.value = r.range(1.1, 2.4);
    const g = ctx.createGain();
    env(g.gain, t0, 0.07 * level, attack, hold, decay);
    src.connect(bp);
    bp.connect(g);
    g.connect(this.dest);
    s.sources.push(src);
    s.nodes.push(bp, g);
  }

  /**
   * One breath. Quiet, ordinary and short — the opposite of the smiler's, which
   * is 2.6 s long and the whole point of that sound. Here it is the thing you
   * only notice afterwards.
   */
  private breath(t0: number): number {
    const ctx = this.g.ctx;
    const r = this.g.rng;
    const s: Scheduled = { at: 0, nodes: [], sources: [] };
    const inDur = r.range(0.3, 0.5);
    const gap = r.range(0.06, 0.16);
    const outDur = r.range(0.36, 0.6);
    const tract = this.timbre.tract;

    // In: quieter, brighter, rising.
    this.airPass(s, t0, inDur, 520 * tract, 1150 * tract, 0.07);
    // Out: a touch louder, falling, with a hint of voice under it — an audible
    // exhale is never entirely silent.
    const t1 = t0 + inDur + gap;
    this.airPass(s, t1, outDur, 900 * tract, 380 * tract, 0.093);
    if (r.chance(0.4)) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      // Floored at 85 Hz. Everything under a bass voice's fundamental belongs
      // to the hound, and a sighing sub-bass is the one note this instrument
      // must never hit.
      const f = Math.max(85, this.timbre.f0 * r.range(0.9, 1.05));
      const end = t1 + outDur;
      osc.frequency.setValueAtTime(f, t1);
      osc.frequency.linearRampToValueAtTime(Math.max(85, f * 0.9), end);
      const g = ctx.createGain();
      env(g.gain, t1, 0.036, 0.09, outDur * 0.4, outDur * 0.5);
      osc.connect(g);
      g.connect(this.dest);
      osc.start(t1);
      osc.stop(end + 0.02);
      s.sources.push(osc);
      s.nodes.push(g);
    }
    return this.commit(s, t1 + outDur);
  }

  /** Filtered air, sweeping. The body of a breath. */
  private airPass(s: Scheduled, t0: number, dur: number, f0: number, f1: number, peak: number): void {
    const ctx = this.g.ctx;
    const attack = dur * 0.4;
    const decay = dur * 0.6;
    const end = t0 + attack + decay;
    const src = this.noise('pink', t0, end);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(clamp(f0, 80, 8000), t0);
    bp.frequency.exponentialRampToValueAtTime(clamp(f1, 80, 8000), end);
    bp.Q.value = 0.9;
    const g = ctx.createGain();
    env(g.gain, t0, peak, attack, 0, decay);
    src.connect(bp);
    bp.connect(g);
    g.connect(this.dest);
    s.sources.push(src);
    s.nodes.push(bp, g);
  }

  /**
   * Somebody shifting their weight: cloth, a shoe turning on carpet, the small
   * noise a person cannot help making by existing in a room. Not a footstep —
   * the game already has four of those and they mean somebody is walking.
   */
  private shift(t0: number): number {
    const ctx = this.g.ctx;
    const r = this.g.rng;
    const s: Scheduled = { at: 0, nodes: [], sources: [] };
    const n = r.int(1, 3);
    let t = t0;
    for (let i = 0; i < n; i++) {
      const dur = r.range(0.09, 0.22);
      const end = t + dur + 0.06;
      const src = this.noise('brown', t, end);
      src.playbackRate.value = r.range(0.7, 1.4);
      // Brown noise under a low-pass is nearly all sub-bass, and sub-bass in an
      // empty corridor reads as something large rather than as a sleeve. The
      // high-pass is what keeps this the size of a person.
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 150;
      hp.Q.value = 0.7;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(r.range(900, 1900), t);
      lp.frequency.exponentialRampToValueAtTime(r.range(300, 560), end);
      lp.Q.value = 0.6;
      const g = ctx.createGain();
      env(g.gain, t, r.range(0.07, 0.13), 0.012, dur * 0.4, dur * 0.6 + 0.05);
      src.connect(hp);
      hp.connect(lp);
      lp.connect(g);
      g.connect(this.dest);
      s.sources.push(src);
      s.nodes.push(hp, lp, g);
      t = end + r.range(0.05, 0.24);
    }
    return this.commit(s, t);
  }

  /** A swallow, a small cough, or the noise of a throat being cleared. */
  private throat(t0: number): number {
    const ctx = this.g.ctx;
    const r = this.g.rng;
    const s: Scheduled = { at: 0, nodes: [], sources: [] };
    const f = Math.max(96, this.timbre.f0 * r.range(0.82, 1.0));
    const dur = r.range(0.1, 0.2);
    const end = t0 + dur + 0.14;

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(f * 1.12, t0);
    // Down a fourth at most, and never under 85 Hz. A grunt that slides further
    // than that stops being a throat and starts being a growl.
    osc.frequency.exponentialRampToValueAtTime(Math.max(85, f * 0.78), end);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = clamp(f * 11, 500, 2600);
    lp.Q.value = 0.7;
    const g = ctx.createGain();
    env(g.gain, t0, 0.055, 0.02, dur, 0.14);
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.dest);
    osc.start(t0);
    osc.stop(end + 0.02);
    s.sources.push(osc);
    s.nodes.push(lp, g);

    // The wet part. Without it this is a note rather than a person.
    const src = this.noise('pink', t0, end);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = r.range(700, 1400) * this.timbre.tract;
    bp.Q.value = 2.2;
    const ng = ctx.createGain();
    env(ng.gain, t0, 0.06, 0.015, dur * 0.8, 0.12);
    src.connect(bp);
    bp.connect(ng);
    ng.connect(this.dest);
    s.sources.push(src);
    s.nodes.push(bp, ng);

    return this.commit(s, end);
  }

  /* ---------------------------------------------------------------- *
   * Plumbing
   * ---------------------------------------------------------------- */

  private noise(kind: 'white' | 'pink' | 'brown', t0: number, end: number): AudioBufferSourceNode {
    const src = this.g.ctx.createBufferSource();
    const buf = this.g.buffer(kind);
    src.buffer = buf;
    src.loop = true;
    src.start(t0, this.g.rng.range(0, Math.max(0.01, buf.duration - 0.7)));
    src.stop(end + 0.02);
    return src;
  }

  private commit(s: Scheduled, end: number): number {
    s.at = end + REAP_PAD;
    this.pending.push(s);
    return end;
  }

  /** Nothing here is pooled, so the nodes have to be let go by hand. */
  private reap(): void {
    const now = this.g.now;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const s = this.pending[i]!;
      if (now < s.at) continue;
      release(s);
      this.pending.splice(i, 1);
    }
  }
}

/** One resonance of the vocal tract, summed in parallel with the others. */
function formant(ctx: AudioContext, src: AudioNode, sum: AudioNode, hz: number, q: number, level: number): void {
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = clamp(hz, 90, 7000);
  bp.Q.value = q;
  const g = ctx.createGain();
  g.gain.value = level;
  src.connect(bp);
  bp.connect(g);
  g.connect(sum);
}

function pickVowel(r: RNG, tract: number): [number, number] {
  let n = r.next() * VOWEL_TOTAL;
  for (const v of VOWELS) {
    n -= v[2];
    if (n <= 0) return [v[0] * tract, v[1] * tract];
  }
  const last = VOWELS[0]!;
  return [last[0] * tract, last[1] * tract];
}

/** Linear attack, hold, exponential decay, pinned to silence at the end. */
function env(p: AudioParam, t0: number, peak: number, attack: number, hold: number, decay: number): void {
  const top = Math.max(0.0002, peak);
  const end = t0 + attack + hold + decay;
  p.setValueAtTime(0.0001, t0);
  p.linearRampToValueAtTime(top, t0 + attack);
  if (hold > 0) p.setValueAtTime(top, t0 + attack + hold);
  p.exponentialRampToValueAtTime(0.0001, end);
  p.setValueAtTime(0, end);
}

function release(s: Scheduled): void {
  for (const src of s.sources) {
    try {
      src.stop();
    } catch {
      /* already finished */
    }
    src.disconnect();
  }
  for (const n of s.nodes) n.disconnect();
  s.sources.length = 0;
  s.nodes.length = 0;
}
